// 자동 생성 기준: 실행계획 6.4 IA·라우트. 화면을 추가하면 이 표와 i18n screens.json 을 같이 고친다.
export type ScreenLayout = 'shell' | 'full';
/** none: 누구나 · guest: 게스트 토큰으로 사용 · member: 로그인 필요 */
export type ScreenAuth = 'none' | 'guest' | 'member';

export interface ScreenDefinition {
  id: string;
  key: string;
  path: string;
  layout: ScreenLayout;
  auth: ScreenAuth;
  /** 개발용 메모 (플레이스홀더에 표시) */
  notes: readonly string[];
}

export const SCREENS = {
  welcome: {
    id: 'C-00',
    key: 'welcome',
    path: '/welcome',
    layout: 'full',
    auth: 'none',
    notes: [],
  },
  home: {
    id: 'C-01',
    key: 'home',
    path: '/',
    layout: 'shell',
    auth: 'none',
    notes: [],
  },
  search: {
    id: 'C-02',
    key: 'search',
    path: '/search',
    layout: 'full',
    auth: 'none',
    notes: [
      '최근·추천 검색어 (국가별 search_suggestion)',
      '통합 결과: 체험·시술·병원·질문 탭',
      '결과 없음 → 호스트 상담 CTA',
    ],
  },
  diagnosis: {
    id: 'C-03',
    key: 'diagnosis',
    path: '/diagnosis/:step',
    layout: 'full',
    auth: 'guest',
    notes: [
      '티켓형 카드(상단 점선 + 하단 고정 NEXT) — kdermpass 진단 레이아웃 참고',
      '6단계: 걱정 Top3 → 시작 의도 → 고민/관심 시술 → 체류 기간 → 예산 → 언어·동행',
      '단계별 자동 저장 PUT /public/v1/diagnoses/{id}/answers/{code}',
    ],
  },
  diagnosisResult: {
    id: 'C-04',
    key: 'diagnosisResult',
    path: '/diagnosis/:id/result',
    layout: 'full',
    auth: 'guest',
    notes: [
      '걱정별 해결 문구 (fear_type)',
      '배정 예정 호스트 카드 + 상담 시작',
      '결과 저장을 위해 로그인 유도 (게스트 병합)',
    ],
  },
  auth: {
    id: 'C-05',
    key: 'auth',
    path: '/auth/:step',
    layout: 'full',
    auth: 'none',
    notes: [
      'step: sign-in · sign-up · verify · reset-password',
      '이메일 OTP → 비밀번호 설정 → 동의 (필수·선택·민감정보)',
      'Apple·Google 버튼 자리 (features.socialLogin, E1) · WeChat E4',
    ],
  },
  explore: {
    id: 'C-06',
    key: 'explore',
    path: '/explore',
    layout: 'shell',
    auth: 'none',
    notes: [
      '탭: Experiences · Treatments · Clinics',
      '지역칩 (GET /public/v1/meta areas) · 카테고리 레일 · 필터 칩',
      '지도 보기 → C-07',
    ],
  },
  exploreMap: {
    id: 'C-07',
    key: 'exploreMap',
    path: '/explore/map',
    layout: 'full',
    auth: 'none',
    notes: [
      'MapProvider 추상화 (D-07)',
      'bbox 조회 + 병원 카드 바텀시트',
      '현재 위치 / 지역 중심 이동',
    ],
  },
  experienceDetail: {
    id: 'C-08',
    key: 'experienceDetail',
    path: '/experiences/:slug',
    layout: 'full',
    auth: 'none',
    notes: [
      '갤러리 · 포함 항목 · 일자별 샘플 일정',
      '병원 · 다운타임 · From 가격 (display)',
      'Secure / 호스트 상담 CTA',
    ],
  },
  treatmentDetail: {
    id: 'C-09',
    key: 'treatmentDetail',
    path: '/treatments/:slug',
    layout: 'full',
    auth: 'none',
    notes: [
      '다운타임 · 통증 · 효과 지속 · 출국 전 권장 일수',
      '병원별 가격 범위 · 가격 보장 배지',
      'FAQ · 관련 질문',
    ],
  },
  clinicDetail: {
    id: 'C-10',
    key: 'clinicDetail',
    path: '/clinics/:slug',
    layout: 'full',
    auth: 'none',
    notes: [
      '의료진 · 지원 언어 · 외국인 가격 보장',
      '시술·가격 목록 · 위치 지도',
      '리뷰 (방문 완료 예약만)',
    ],
  },
  questions: {
    id: 'C-11',
    key: 'questions',
    path: '/questions',
    layout: 'shell',
    auth: 'none',
    notes: [
      '세그먼트 필터 (국가·연령대·고민)',
      '질문 카드 + 하트 · 호스트 답변',
      '질문 등록 (로그인, 검수 후 공개)',
    ],
  },
  questionDetail: {
    id: 'C-11',
    key: 'questionDetail',
    path: '/questions/:id',
    layout: 'full',
    auth: 'none',
    notes: ['질문 · 호스트 답변 · 관련 시술', '이 질문으로 상담 시작'],
  },
  journeys: {
    id: 'C-12',
    key: 'journeys',
    path: '/journeys',
    layout: 'shell',
    auth: 'none',
    notes: [
      '스토리 카드 (사진 콜라주 · 여행자 · 태그 · 인용)',
      '고민·기간 필터',
    ],
  },
  journeyDetail: {
    id: 'C-12',
    key: 'journeyDetail',
    path: '/journeys/:id',
    layout: 'full',
    auth: 'none',
    notes: [
      '일자별 단계 (story_stage)',
      '사용한 체험·시술 · 같은 플랜으로 상담',
    ],
  },
  trends: {
    id: 'C-13',
    key: 'trends',
    path: '/trends',
    layout: 'shell',
    auth: 'none',
    notes: [
      '카테고리 탭 (Beauty·Travel·Food·Culture)',
      '트렌드 카드 · 병원 추천 시술 포함',
    ],
  },
  trendDetail: {
    id: 'C-13',
    key: 'trendDetail',
    path: '/trends/:slug',
    layout: 'full',
    auth: 'none',
    notes: ['본문 · 관련 시술·체험 카드', 'SEO 메타 (react-helmet-async)'],
  },
  chat: {
    id: 'C-14',
    key: 'chat',
    path: '/chat',
    layout: 'shell',
    auth: 'none',
    notes: [
      'My Conversations (Host · Clinic · AI)',
      '빠른 의도 6개 (quick_reply_template CLIENT_INTENT)',
      '진입 시 로그인 유도',
    ],
  },
  chatRoom: {
    id: 'C-15',
    key: 'chatRoom',
    path: '/chat/:roomId',
    layout: 'full',
    auth: 'member',
    notes: [
      '메시지 커서 조회 · 낙관적 전송 (clientMessageId)',
      '구조화 카드: 여행일·고민·예산 폼, 플랜, 결제 링크',
      '실시간 /topic/glow/rooms/{id} (P3) · 번역 토글 E1',
    ],
  },
  plans: {
    id: 'C-16',
    key: 'plans',
    path: '/plans/:proposalSetId',
    layout: 'full',
    auth: 'member',
    notes: [
      '최대 3개: Best Match · Best Value · Premium Choice',
      '옵션별 Safe Schedule · 추천 이유 · 가격 보장',
      '옵션 선택 → 주문 초안',
    ],
  },
  products: {
    id: 'C-17A',
    key: 'products',
    path: '/passes',
    layout: 'full',
    auth: 'none',
    notes: ['현재 게시 상품 자동 목록 · 원화 가격/혜택 · 실제 상세 연결'],
  },
  productDetail: {
    id: 'C-17AD',
    key: 'productDetail',
    path: '/passes/:id',
    layout: 'full',
    auth: 'none',
    notes: ['게시된 조건과 원래 환불 문서 · 명시 상담 초안 연결'],
  },
  checkout: {
    id: 'C-17',
    key: 'checkout',
    path: '/checkout/:orderId',
    layout: 'full',
    auth: 'member',
    notes: [
      'Care Deposit · Journey Voucher · 멤버십',
      '정책 요약 (변경·취소·환불)',
      'MVP: MANUAL 입금 안내 / PG 수단은 E1 (features.pgPayment)',
    ],
  },
  checkoutComplete: {
    id: 'C-18',
    key: 'checkoutComplete',
    path: '/checkout/:orderId/complete',
    layout: 'full',
    auth: 'member',
    notes: ['바우처 번호 · 다음 단계 (호스트 연락)', 'My Trip 바로가기'],
  },
  trip: {
    id: 'C-19',
    key: 'trip',
    path: '/trip',
    layout: 'shell',
    auth: 'member',
    notes: [
      'D-day · 진행 단계 · 오늘 일정',
      '예약 · 여행 정보 · 사후관리 바로가기',
    ],
  },
  itinerary: {
    id: 'C-20',
    key: 'itinerary',
    path: '/trip/:tripId/itinerary',
    layout: 'full',
    auth: 'member',
    notes: [
      '일자 탭 + 타임라인 (Treatment·Escort·Dining·Transfer·Recovery·Sightseeing)',
      'Safe Schedule 스트립: 입국 → 시술 → 회복 → 관광 → 최종 체크 → 출국',
      'KST + (your time) 병기',
    ],
  },
  planner: {
    id: 'C-21',
    key: 'planner',
    path: '/planner',
    layout: 'full',
    auth: 'guest',
    notes: [
      '기간·예산·고민 입력',
      'MVP: 체험 템플릿 규칙 매칭 / E3: LLM',
      '결과를 호스트 상담으로 이어가기',
    ],
  },
  plannerInbox: {
    id: 'C-21-INBOX',
    key: 'plannerInbox',
    path: '/chat/planner',
    layout: 'full',
    auth: 'guest',
    notes: ['본인 샘플 상담 metadata·페이지와 결과 열람 진입'],
  },
  plannerConversation: {
    id: 'C-21-ROOM',
    key: 'plannerConversation',
    path: '/chat/planner/:id',
    layout: 'full',
    auth: 'guest',
    notes: ['고정 샘플 결과와 원래 HOST 상담 연결'],
  },
  bookings: {
    id: 'C-22',
    key: 'bookings',
    path: '/trip/bookings',
    layout: 'shell',
    auth: 'member',
    notes: ['예약 상태 칩 · 병원 · 일시 (KST)', '변경·취소 요청'],
  },
  bookingDetail: {
    id: 'C-22',
    key: 'bookingDetail',
    path: '/bookings/:id',
    layout: 'full',
    auth: 'member',
    notes: [
      '체크리스트 · 위치 · 가격 보장',
      '변경 요청 (Care Deposit 1회 무료) · 취소 요청',
    ],
  },
  travelInfo: {
    id: 'C-23',
    key: 'travelInfo',
    path: '/trip/travel-info',
    layout: 'full',
    auth: 'member',
    notes: ['항공편 · 호텔 · 동행자 · 비상연락처', '입국 체크리스트'],
  },
  saved: {
    id: 'C-24',
    key: 'saved',
    path: '/my/saved',
    layout: 'shell',
    auth: 'member',
    notes: ['찜 목록 (체험·시술·병원·질문·스토리)'],
  },
  recent: {
    id: 'C-24',
    key: 'recent',
    path: '/my/recent',
    layout: 'shell',
    auth: 'member',
    notes: ['최근 본 항목 50개'],
  },
  aftercareList: {
    id: 'C-25',
    key: 'aftercareList',
    path: '/aftercare',
    layout: 'full',
    auth: 'member',
    notes: ['사후관리 목록·빈 상태·완료 방문 연결'],
  },
  aftercare: {
    id: 'C-25',
    key: 'aftercare',
    path: '/aftercare/:planId',
    layout: 'full',
    auth: 'member',
    notes: [
      'D+1·3·7·30 체크인 (상태·증상·사진)',
      'Something feels wrong → 긴급 도움 (에스컬레이션)',
    ],
  },
  my: {
    id: 'C-26',
    key: 'my',
    path: '/my',
    layout: 'shell',
    auth: 'none',
    notes: [],
  },
  settings: {
    id: 'C-27',
    key: 'settings',
    path: '/my/settings/:section?',
    layout: 'full',
    auth: 'member',
    notes: ['프로필 · 언어·통화 · 알림 · 동의 관리', '데이터 내보내기 · 탈퇴'],
  },
  membership: {
    id: 'C-28',
    key: 'membership',
    path: '/membership',
    layout: 'full',
    auth: 'none',
    notes: ['Glow Care vs Glow Concierge 비교', '가입·업그레이드'],
  },
  points: {
    id: 'C-29',
    key: 'points',
    path: '/my/points',
    layout: 'full',
    auth: 'member',
    notes: ['포인트 원장 · 만료 예정'],
  },
  vouchers: {
    id: 'C-29',
    key: 'vouchers',
    path: '/my/vouchers',
    layout: 'full',
    auth: 'member',
    notes: ['본인 완료 바우처 기본 목록 · 원래 기한/잔여 혜택'],
  },
  voucherDetail: {
    id: 'C-29',
    key: 'voucherDetail',
    path: '/my/vouchers/:id',
    layout: 'full',
    auth: 'member',
    notes: ['본인 바우처 상세 · 서비스 요청/미확정 취소·원래 Care'],
  },
  voucherOrder: {
    id: 'C-29',
    key: 'voucherOrder',
    path: '/my/vouchers/orders/:orderId',
    layout: 'full',
    auth: 'member',
    notes: ['원래 입금 완료 주문의 실제 발급 바우처 연결'],
  },
  coupons: {
    id: 'C-29',
    key: 'coupons',
    path: '/my/coupons',
    layout: 'full',
    auth: 'member',
    notes: ['쿠폰함 · 사용 조건'],
  },
  reviews: {
    id: 'C-29',
    key: 'reviews',
    path: '/my/reviews',
    layout: 'full',
    auth: 'member',
    notes: ['작성한 리뷰 · 작성 가능한 예약'],
  },
  commerceRefund: {
    id: 'C-29',
    key: 'commerceRefund',
    path: '/my/commerce-refunds/:sourceType/:sourceId',
    layout: 'full',
    auth: 'member',
    notes: ['일반 주문·멤버십 본인 환불 접수·제안/결정·철회·완료'],
  },
  payments: {
    id: 'C-29',
    key: 'payments',
    path: '/my/payments',
    layout: 'full',
    auth: 'member',
    notes: ['주문·결제·환불 내역 · 영수증'],
  },
  bookingReview: {
    id: 'C-29',
    key: 'bookingReview',
    path: '/bookings/:id/review',
    layout: 'full',
    auth: 'member',
    notes: ['병원 별점 · 호스트 만족도 · 사진', '원문 언어 저장 (번역 E1)'],
  },
  notifications: {
    id: 'C-30',
    key: 'notifications',
    path: '/notifications',
    layout: 'full',
    auth: 'member',
    notes: ['카테고리 탭 · 읽음 처리'],
  },
  help: {
    id: 'C-31',
    key: 'help',
    path: '/help',
    layout: 'full',
    auth: 'none',
    notes: ['FAQ · 호스트 상담 · 긴급 연락'],
  },
  legal: {
    id: 'C-32',
    key: 'legal',
    path: '/legal/:type',
    layout: 'full',
    auth: 'none',
    notes: [
      'type: terms · privacy · refund · patient-rights · health · cross-border · marketing · cancellation',
      'GET /public/v1/policies/{type}',
    ],
  },
  notFound: {
    id: 'C-33',
    key: 'notFound',
    path: '*',
    layout: 'full',
    auth: 'none',
    notes: [
      '404·라우트 렌더링 오류·점검·연결 끊김 안내와 복귀',
      '공개 service-status 조회, 점검 중 Help/Legal 읽기 유지',
    ],
  },
} as const satisfies Record<string, ScreenDefinition>;

export type ScreenKey = keyof typeof SCREENS;
